import { inject, Injectable } from '@angular/core';
import { EjournalApiService } from '@et/api';
import { NotificationService } from '@et/notifications';
import { EtJournalEntry, EtJournalESeal } from '@et/typings';
import { Store } from '@ngxs/store';
import { catchError, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { UpdateEntries } from '../state/ejournal-session.actions';
import { EjournalSessionSelectors } from '../state/ejournal-session.selectors';
import { EjournalEntriesService } from './ejournal-entries.service';
import { EjournalSessionCancelService } from './ejournal-session-cancel.service';
import { EsealsConverterService } from './eseals-converter.service';

@Injectable()
export class EjournalSealsService {
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly esealsConverter = inject(EsealsConverterService);
  private readonly entriesService = inject(EjournalEntriesService);
  private readonly sessionCancel = inject(EjournalSessionCancelService);
  private readonly store = inject(Store);

  private readonly eJournal = this.store.selectSignal(
    EjournalSessionSelectors.getEjournal,
  );
  private readonly signers = this.store.selectSignal(
    EjournalSessionSelectors.getSigners,
  );
  private readonly entries = this.store.selectSignal(
    EjournalSessionSelectors.getEntries,
  );
  private readonly notarizedAuditActions = this.store.selectSignal(
    EjournalSessionSelectors.getNotarizedAuditActions,
  );
  private readonly notarizedAuditLoaded = this.store.selectSignal(
    EjournalSessionSelectors.getNotarizedAuditLoaded,
  );
  private readonly pageNamesByNumber = this.store.selectSignal(
    EjournalSessionSelectors.getPageNamesByNumber,
  );

  /**
   * Derives seal/eSeal cards from entries, signers, and notarized audit actions.
   */
  getSeals(): EtJournalESeal[] {
    const ejournal = this.eJournal();
    return this.esealsConverter.toSeals({
      entries: this.entries(),
      signers: this.signers(),
      notarizedActions: this.notarizedAuditActions(),
      packetId: ejournal?.packetId ?? '',
      eJournalPacketId: ejournal?.id ?? '',
      pageNamesByNumber: this.pageNamesByNumber(),
      auditActionsLoaded: this.notarizedAuditLoaded(),
      defaultFee: ejournal?.feeCharged ?? 0,
    });
  }

  /**
   * Cancellation-act entries that are not grouped into seals.
   */
  getCancelActEntries(): EtJournalEntry[] {
    return this.entries().filter((e) => !!e.cancelActUtcDate);
  }

  /**
   * Saves a seal/eSeal by ensuring a holder entry exists, then apply signer/entry ops.
   */
  saveSeal(nextSeal: EtJournalESeal): Observable<unknown> {
    const normalizedDate = this.esealsConverter.normalizeNotarizationDate(
      nextSeal.notarizationDate,
    );
    const seal: EtJournalESeal = {
      ...nextSeal,
      notarizationDate: normalizedDate,
    };

    const signers = this.signers();
    const previousSeal =
      this.getSeals().find((s) => s.sealId === nextSeal.sealId) ??
      (nextSeal.holderEntry
        ? this.getSeals().find(
            (s) =>
              s.holderEntry?.eJournalEntryId ===
              nextSeal.holderEntry?.eJournalEntryId,
          )
        : null) ??
      null;
    const ops = this.esealsConverter.diffSealSave(previousSeal, seal, signers);

    const deleteIds = ops.delete.map((d) => d.entry.eJournalEntryId);
    const delete$ =
      deleteIds.length > 0
        ? this.entriesService
            .unlinkEntriesFromSigners(deleteIds)
            .pipe(
              switchMap(() =>
                this.ejournalApiService
                  .deleteEntries(deleteIds)
                  .pipe(
                    tap(() =>
                      this.entriesService.removeEntriesFromLocalState(
                        deleteIds,
                      ),
                    ),
                  ),
              ),
            )
        : of(null);

    const patch$ =
      ops.patch.length > 0
        ? forkJoin(
            ops.patch.map((entry) => {
              const payload = this.entriesService.entryToWithId(entry);
              return this.ejournalApiService
                .patchEjournalEntry(entry.eJournalEntryId, payload)
                .pipe(
                  tap((updated) => {
                    const mapped = this.entriesService.withIdToEntry(updated);
                    this.store.dispatch(
                      new UpdateEntries((entries) =>
                        entries.map((e) =>
                          e.eJournalEntryId === mapped.eJournalEntryId
                            ? mapped
                            : e,
                        ),
                      ),
                    );
                  }),
                );
            }),
          )
        : of([]);

    return delete$.pipe(
      this.sessionCancel.takeUntilCancel(),
      switchMap(() => patch$),
      switchMap(() =>
        ops.createHolder
          ? this.entriesService.createHolderEntry(seal)
          : of(null),
      ),
      switchMap(() => this.entriesService.createSealEntries(seal, ops.create)),
      catchError((error) => {
        this.notificationService.showError('Failed to save seal');
        throw error;
      }),
    );
  }

  /**
   * Deletes holder + signer entries for a seal. Virtual audit eSeals (no entries) no-op.
   */
  deleteSeal(seal: EtJournalESeal): Observable<unknown> {
    const entryIds = [
      ...(seal.holderEntry ? [seal.holderEntry.eJournalEntryId] : []),
      ...seal.entries.map((e) => e.eJournalEntryId),
    ];
    if (entryIds.length === 0) {
      return of(null);
    }

    return this.entriesService.unlinkEntriesFromSigners(entryIds).pipe(
      this.sessionCancel.takeUntilCancel(),
      switchMap(() => this.ejournalApiService.deleteEntries(entryIds)),
      tap(() => {
        this.notificationService.showSuccess('Seal deleted successfully');
        this.entriesService.removeEntriesFromLocalState(entryIds);
      }),
      catchError((error) => {
        this.notificationService.showError('Failed to delete seal');
        throw error;
      }),
    );
  }
}
